import React, { useState } from 'react';
import { Download, Chrome, Puzzle, ShieldCheck, HardDrive, EyeOff } from 'lucide-react';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';

export function ExtensionPromo() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors h-8 px-3 rounded-md cursor-pointer border border-emerald-200/50 dark:border-emerald-800/50 shadow-sm"
        aria-label="Download Extension"
      >
        <Puzzle className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Extension</span>
      </button>

      <ResponsiveOverlay isOpen={isOpen} onClose={() => setIsOpen(false)} variant="modal" className="sm:max-w-xl w-full">
        <div className="p-6 md:p-8 flex flex-col items-center flex-shrink-0 min-h-0 overflow-y-auto max-h-[85vh]">
          <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-2xl flex flex-shrink-0 items-center justify-center mb-5 rotate-3 shadow-sm border border-emerald-200 dark:border-emerald-800/50">
            <Puzzle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
          </div>
          
          <h2 className="text-xl md:text-2xl font-bold text-slate-800 text-center dark:text-slate-100 mb-2">ResumePro Browser Extension</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6 max-w-lg text-center leading-relaxed">
            Supercharge your job search. Extract job descriptions from LinkedIn & Indeed with a single click, and auto-fill complex applications using your tailored resume.
          </p>

          <div className="w-full flex flex-col gap-4 mb-6">
            <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 md:p-5 text-left border border-slate-200 dark:border-slate-800 flex flex-col">
              <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-3 text-sm flex items-center gap-2">
                <Chrome className="w-4 h-4 text-emerald-600 dark:text-emerald-500" /> Quick Install
              </h3>
              <ol className="text-[13px] text-slate-600 dark:text-slate-400 space-y-2.5 list-none pl-1 m-0">
                <li className="flex gap-2 items-start"><span className="text-slate-400 font-mono text-xs w-3 shrink-0">1.</span> <span><span className="font-medium text-slate-700 dark:text-slate-300">Download</span> and extract the ZIP below.</span></li>
                <li className="flex gap-2 items-start"><span className="text-slate-400 font-mono text-xs w-3 shrink-0">2.</span> <span>Go to <code className="bg-slate-200 dark:bg-slate-800 px-1 py-0.5 rounded text-[11px] font-mono break-all whitespace-nowrap">chrome://extensions</code></span></li>
                <li className="flex gap-2 items-start"><span className="text-slate-400 font-mono text-xs w-3 shrink-0">3.</span> <span>Enable <strong className="font-medium text-slate-700 dark:text-slate-300">Developer mode</strong> (top right).</span></li>
                <li className="flex gap-2 items-start"><span className="text-slate-400 font-mono text-xs w-3 shrink-0">4.</span> <span>Click <strong className="font-medium text-slate-700 dark:text-slate-300">Load unpacked</strong> & select folder.</span></li>
              </ol>
            </div>
            
            <div className="bg-emerald-50/50 dark:bg-emerald-900/10 rounded-xl p-4 md:p-5 text-left border border-emerald-100 dark:border-emerald-800/30 flex flex-col">
              <h3 className="font-semibold text-emerald-800 dark:text-emerald-300 mb-3 text-sm flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-500" /> Privacy & Security Guarantee
              </h3>
              <ul className="text-[13px] text-slate-600 dark:text-slate-400 space-y-3 m-0 pl-1 list-none">
                <li className="flex items-start gap-2">
                  <HardDrive className="w-4 h-4 text-emerald-600/70 mt-0.5 shrink-0" />
                  <span><strong className="font-medium text-slate-700 dark:text-slate-300 line-clamp-1 block mb-0.5">100% Local Storage</strong> Your profile data is written strictly to your browser's local sandbox, never synced remotely.</span>
                </li>
                <li className="flex items-start gap-2">
                  <EyeOff className="w-4 h-4 text-emerald-600/70 mt-0.5 shrink-0" />
                  <span><strong className="font-medium text-slate-700 dark:text-slate-300 line-clamp-1 block mb-0.5">Zero Tracking</strong> We don't harvest inputs, log your browsing history, or include any telemetry/analytics.</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="w-full flex flex-col sm:flex-row-reverse gap-3 mt-2">
            <a
              href="/ResumePro-Extension.zip"
              download="ResumePro-Extension.zip"
              onClick={() => setIsOpen(false)}
              className="w-full sm:flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold py-3.5 px-6 rounded-xl transition-colors cursor-pointer shadow-sm active:scale-[0.98] text-sm sm:text-base"
            >
              <Download className="w-4 h-4" />
              Download
            </a>
            
            <button 
              onClick={() => setIsOpen(false)}
              className="w-full sm:flex-1 py-3.5 px-6 text-sm font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-xl transition-colors border border-slate-200 dark:border-slate-800 bg-white dark:bg-transparent cursor-pointer"
            >
              Dismiss
            </button>
          </div>
        </div>
      </ResponsiveOverlay>
    </>
  );
}
